import { Store } from '../net/store'

/**
 * Volumes (0..1) and mute (PHASE3 T3.6, D-051), kept in localStorage `runkr.audio`. T3.7's
 * sliders read and write this store; the engine follows it.
 */
export interface AudioSettings {
  master: number
  music: number
  sfx: number
  muted: boolean
}

export const AUDIO_KEY = 'runkr.audio'

export const DEFAULT_AUDIO: Readonly<AudioSettings> = {
  master: 0.8,
  music: 0.5,
  sfx: 1,
  muted: false,
}

/** Storage that may be missing or throw (private mode, blocked site data). */
export type AudioStorage = Pick<Storage, 'getItem' | 'setItem'> | null

/** A volume in 0..1; anything that is not a finite number becomes `fallback`. */
export function clampVolume(v: unknown, fallback: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback
  return Math.min(1, Math.max(0, v))
}

/** Settings from a stored JSON string; missing or broken fields keep their defaults. */
export function parseAudioSettings(raw: string | null | undefined): AudioSettings {
  let v: unknown
  try {
    v = raw ? JSON.parse(raw) : null
  } catch {
    v = null
  }
  const o = typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {}
  return {
    master: clampVolume(o.master, DEFAULT_AUDIO.master),
    music: clampVolume(o.music, DEFAULT_AUDIO.music),
    sfx: clampVolume(o.sfx, DEFAULT_AUDIO.sfx),
    muted: typeof o.muted === 'boolean' ? o.muted : DEFAULT_AUDIO.muted,
  }
}

export interface AudioSettingsStore {
  readonly store: Store<AudioSettings>
  /** Change some fields (volumes clamped) and save. */
  set(patch: Partial<AudioSettings>): void
  toggleMute(): void
}

export function createAudioSettings(storage: AudioStorage): AudioSettingsStore {
  let raw: string | null
  try {
    raw = storage?.getItem(AUDIO_KEY) ?? null
  } catch {
    raw = null
  }
  const store = new Store(parseAudioSettings(raw))
  const set = (patch: Partial<AudioSettings>): void => {
    const cur = store.get()
    const next: AudioSettings = {
      master: clampVolume(patch.master ?? cur.master, cur.master),
      music: clampVolume(patch.music ?? cur.music, cur.music),
      sfx: clampVolume(patch.sfx ?? cur.sfx, cur.sfx),
      muted: patch.muted ?? cur.muted,
    }
    store.set(next)
    try {
      storage?.setItem(AUDIO_KEY, JSON.stringify(next))
    } catch {
      // No storage: the change lasts for this page only.
    }
  }
  return { store, set, toggleMute: () => set({ muted: !store.get().muted }) }
}

/** The page's localStorage, or null where reading it throws. */
export function browserStorage(): AudioStorage {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}
